import { Subject } from 'rxjs';
import * as shareApi from '@/api/share';
import * as fileApi from '@/api/file';
import type { SharedWithMeEntry } from '@/api/share';
import type { FileInfo } from '@/api/file';
import useFileStore from '@/utils/fileStore';

export type SharedWithMeEntryResolved = SharedWithMeEntry & {
  fileTree?: FileInfo;
};

class ShareWithMeService {
  public shares: SharedWithMeEntryResolved[] = [];
  public isLoading = false;

  private sharesSubject = new Subject<SharedWithMeEntryResolved[]>();
  public shares$ = this.sharesSubject.asObservable();

  async initialize(): Promise<void> {
    this.isLoading = true;

    try {
      const res = await shareApi.getShareWithMe();
      if (!res?.ok || !res.data) {
        this.shares = [];
        return;
      }

      const entries = res.data;

      const resolved = await Promise.all(
        entries.map(async (entry): Promise<SharedWithMeEntryResolved> => {
          // If the server already provided a file tree, use it directly
          if (entry.file_tree != null) {
            return { ...entry, fileTree: entry.file_tree as FileInfo };
          }

          // Otherwise fetch the file tree from the file resource API
          const filePath = entry.share.file_path;
          const fileRes = await fileApi.getFileResource(filePath, useFileStore.getState().includeHidden);
          return {
            ...entry,
            fileTree: fileRes?.data ?? undefined,
          };
        })
      );

      this.shares = resolved;
      this.sharesSubject.next(this.shares);
    } catch (e) {
      console.error('[ShareWithMeService] Failed to initialize:', e);
      this.shares = [];
    } finally {
      this.isLoading = false;
    }
  }

  async refresh(): Promise<void> {
    await this.initialize();
  }

  destroy(): void {
    this.sharesSubject.complete();
  }
}

export const shareWithMeService = new ShareWithMeService();
